import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { AlertTriangle, LoaderCircle, Network, Plus, RefreshCcw, ShieldCheck, Unlink } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  clearPending, createOperation, isUncertain, joinWachen, networkError,
  instanceAvailability, networkMembershipLabel, networkRequest, readPending, savePending,
} from "@/lib/networkManagement";

export default function Netzwerke() {
  const [status, setStatus] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [wachen, setWachen] = useState([]);
  const [password, setPassword] = useState("");
  const [networkName, setNetworkName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const operationRunning = useRef(false);
  const pending = status?.owner_id ? readPending(status.owner_id) : null;

  const load = useCallback(async () => {
    setError("");
    try {
      const nextStatus = await networkRequest("GET", "/status");
      setStatus(nextStatus);
      if (nextStatus.authenticated) {
        const [nextCatalog, nextWachen] = await Promise.all([
          networkRequest("GET", "/catalog"), networkRequest("GET", "/wachen"),
        ]);
        setCatalog(nextCatalog);
        setWachen(Array.isArray(nextWachen) ? nextWachen : []);
      } else {
        setCatalog(null);
      }
    } catch (requestError) {
      setError(networkError(requestError));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirmPassword = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const login = await axios.post(`${API}/auth/login`, { username: status.username, password });
      localStorage.setItem("token", login.data.access_token);
      const session = await networkRequest("POST", "/session");
      setPassword("");
      setStatus((current) => ({ ...current, authenticated: true, csrf_token: session.csrf_token }));
      await load();
    } catch (requestError) {
      toast.error(networkError(requestError));
    } finally { setBusy(false); }
  };

  const runOperation = async (operation) => {
    if (operationRunning.current) return;
    operationRunning.current = true;
    setBusy(true);
    savePending(status.owner_id, operation);
    try {
      await networkRequest(operation.method, operation.path, operation.body, status.csrf_token);
      clearPending(status.owner_id);
      toast.success("Änderung durch den Netzwerkdienst bestätigt");
      await load();
    } catch (requestError) {
      if (!isUncertain(requestError)) clearPending(status.owner_id);
      toast.error(networkError(requestError));
      if (requestError.response?.status === 401) setStatus((current) => ({ ...current, authenticated: false }));
      if (requestError.response?.status === 409) await load();
    } finally { operationRunning.current = false; setBusy(false); }
  };

  const rows = useMemo(() => catalog ? joinWachen(wachen, catalog) : [], [wachen, catalog]);
  const assignableRows = useMemo(() => rows.filter((row) =>
    row.instance && !row.network && instanceAvailability(row.instance) === "online"
  ), [rows]);

  if (!status && !error) return <div className="min-h-[400px] flex items-center justify-center"><LoaderCircle className="animate-spin" /></div>;
  if (error) return <State title="Netzwerkverwaltung nicht verfügbar" text={error} action={load} />;
  if (!status.enabled) return <State title="Netzwerkverwaltung ist deaktiviert" text="Die bisherigen Payment-Funktionen stehen unverändert zur Verfügung." />;
  if (!status.configured) return <State title="Einrichtung unvollständig" text="Zertifikate, Betreiber-ID oder private Netzwerkverbindung fehlen." />;
  if (!status.authenticated) return (
    <div className="max-w-lg space-y-5">
      <h1 className="text-4xl font-bold text-gray-900">Netzwerke</h1>
      <Card><CardHeader><CardTitle className="flex gap-2"><ShieldCheck /> Betreiber bestätigen</CardTitle></CardHeader>
        <CardContent><form className="space-y-4" onSubmit={confirmPassword}>
          <p className="text-sm text-gray-600">Bitte bestätige die Netzwerkverwaltung erneut mit deinem Passwort.</p>
          <div><Label htmlFor="network-password">Passwort</Label><Input id="network-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} /></div>
          <Button disabled={busy}>{busy ? "Wird geprüft…" : "Bestätigen"}</Button>
        </form></CardContent></Card>
    </div>
  );
  if (!catalog) return <State title="Katalog konnte nicht geladen werden" text="Der Netzwerkdienst lieferte keinen bestätigten Katalog." action={load} />;

  return <div className="space-y-6">
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
      <div><h1 className="text-4xl font-bold text-gray-900">Netzwerke</h1><p className="text-gray-600 mt-1">Kantineninstanzen sicher gruppieren</p></div>
      <Button variant="outline" onClick={load}><RefreshCcw className="w-4 h-4 mr-2" />Aktualisieren</Button>
    </div>
    {pending && <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
      <div><p className="font-semibold text-amber-950">Ergebnis der letzten Änderung ist ungeklärt</p><p className="text-sm text-amber-900">Die identische Anfrage muss wiederholt werden.</p></div>
      <Button disabled={busy} onClick={() => runOperation(pending)}><RefreshCcw className="w-4 h-4 mr-2" />Identisch wiederholen</Button>
    </div>}
    <Card><CardHeader><CardTitle>Netzwerk erstellen</CardTitle></CardHeader><CardContent>
      <form className="flex flex-col sm:flex-row gap-3" onSubmit={(e) => { e.preventDefault(); const name=networkName.trim(); if (!name) return; setNetworkName(""); runOperation(createOperation("POST", "/networks", {name}, catalog.revision)); }}>
        <Input aria-label="Netzwerkname" maxLength={80} value={networkName} onChange={(e) => setNetworkName(e.target.value)} placeholder="z. B. BF Berlin" />
        <Button disabled={busy || Boolean(pending)}><Plus className="w-4 h-4 mr-2" />Erstellen</Button>
      </form>
    </CardContent></Card>
    <Card><CardHeader><CardTitle>Wache einem Netzwerk zuordnen</CardTitle></CardHeader><CardContent className="space-y-3">
      <p className="text-sm text-gray-600">Hier erscheinen online registrierte Wachen, die noch keinem Netzwerk angehören.</p>
      {catalog.networks.length === 0 ? <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Lege zuerst ein Netzwerk an.</p> : assignableRows.length === 0 ? <p className="rounded-xl border p-3 text-sm text-gray-600">Aktuell ist keine unzugeordnete Online-Wache verfügbar. Neue Wachen erscheinen hier nach ihrer Registrierung mit FW-Kantine 1.22.1.</p> : assignableRows.map((row) => <div key={row.wache.id} className="rounded-xl border p-3 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div><p className="font-medium">{row.wache.name}</p><p className="text-sm text-gray-600">Bereit zur Zuordnung</p></div>
        <select aria-label={`${row.wache.name} zuordnen`} className="border rounded-lg px-3 py-2" disabled={busy || Boolean(pending)} defaultValue="" onChange={(e) => e.target.value && runOperation(createOperation("PUT", `/instances/${row.instance.instance_id}/membership`, {network_id:e.target.value}, catalog.revision))}><option value="">Netzwerk wählen</option>{catalog.networks.map((network) => <option key={network.id} value={network.id}>{network.name}</option>)}</select>
      </div>)}
    </CardContent></Card>
    <div className="grid xl:grid-cols-2 gap-4">
      {catalog.networks.map((network) => {
        const members = rows.filter((row) => row.instance?.network_id === network.id);
        return <Card key={network.id}><CardHeader><CardTitle className="flex items-center justify-between"><span className="flex gap-2"><Network />{network.name}</span><span className="text-sm font-normal text-gray-500">{members.length} Wachen</span></CardTitle></CardHeader>
          <CardContent className="space-y-3">{members.length === 0 ? <p className="text-sm text-gray-500">Noch keine Wache zugeordnet</p> : members.map((row) => <div key={row.wache.id} className="rounded-xl border p-3 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div><p className="font-medium">{row.wache.name}</p><p className="text-xs text-gray-500">{instanceAvailability(row.instance) === "online" ? "Online" : "Offline"}</p></div>
            <Button variant="outline" disabled={busy || Boolean(pending)} onClick={() => window.confirm("Mitgliedschaft entfernen? Lokale Buchungen bleiben erhalten, der Netzwerkaustausch endet.") && runOperation(createOperation("PUT", `/instances/${row.instance.instance_id}/membership`, {network_id:null}, catalog.revision))}><Unlink className="w-4 h-4 mr-2" />Entfernen</Button>
          </div>)}</CardContent></Card>;
      })}
    </div>
    <Card><CardHeader><CardTitle>Status aller Wachen</CardTitle></CardHeader><CardContent className="space-y-3">{rows.map((row) => <div key={row.wache.id} className="rounded-xl border p-3 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
      <div><p className="font-medium">{row.wache.name}</p><p className="text-sm text-gray-600">{networkMembershipLabel(row.instance, row.network)}</p></div>
    </div>)}</CardContent></Card>
  </div>;
}

function State({ title, text, action }) {
  return <div className="max-w-xl rounded-xl border bg-white p-6"><AlertTriangle className="text-amber-600 mb-3" /><h1 className="text-2xl font-bold">{title}</h1><p className="text-gray-600 mt-2">{text}</p>{action && <Button className="mt-4" onClick={action}>Erneut versuchen</Button>}</div>;
}
